import { httpPost, httpDownload, httpGet, httpPostFile } from '../../../utils/api.utils';
import { DEFAULT_TABLE_RESULT_MODEL, REPORT_COLUMN_MODULE } from '../../../constants/external-constants';
import { convertToFormatLocalDate, convertToFormatTimeZoneDate } from '../../../utils/helpers.utils';

import {
    getBillingAccount 
    , setDefaultBillingAccount
    , setBillingAccountError
    , setBillingAccountColSearchStr
    , sortBillingAccountList
    , setBillingAccountListPageSizes
    , setBillingAccountFilter
    , applyBillingAccountFilter
    , getBillingComments
    , insertBillingComments
    , resetBillingComments
    , deleteBillingAccountComments
} from '../../../constants/model-reducers/external/finance/billing-account.settings';

const defaultState = () => ({
    isBillingAccountLoaded: false,
    isBillingAccountError: false,
    billingAccountItems: DEFAULT_TABLE_RESULT_MODEL,
    billingAccountColSort: '',
    billingAccountIsSortedAsc: true,
    //Manage User Filters
    billingAccountFilterData: [],
    billingAccountFilterLoaded: false,
    billingAccountTableFilterList: [],
    billingAccountTableFilterApply: false,
    billingComments: [],
    pageIndex: 1,
    hasMoreData: false
});

const initialState = defaultState();

export const billingAccountActions = {
    setDefaultBillingAccount: () => async (dispatch, state) => {
        dispatch({
            type: setDefaultBillingAccount,
            billingAccountItems: DEFAULT_TABLE_RESULT_MODEL
        });
    },

    getBillingAccount: (pageIndex = 0, pageUnmount = false) => async (dispatch, state) => {
        const { Headers, Pagination } = state().billingAccount.billingAccountItems;
        const { billingAccountColSort, billingAccountIsSortedAsc, billingAccountTableFilterList, billingAccountTableFilterApply } = state().billingAccount;

        try {
            const headersToSend = pageUnmount ? {} : { ...Headers };
            var data = {
                PageFilter: {
                    PageIndex: pageIndex !== undefined ? pageIndex : 0,
                    PageSize: Pagination.PerPage,
                    PageSizes: Pagination.PageSizes
                },
                Eid: JSON.parse(sessionStorage.getItem('User')).Employee.EnterpriseId,
                UserId: sessionStorage.getItem('UserID'),
                EmployeeId: sessionStorage.getItem('EmployeeID'),
                Headers: headersToSend,
                SortedColumnName: billingAccountColSort,
                IsSortAscending: billingAccountIsSortedAsc,
                TableFilterList: billingAccountTableFilterList,
                ApplyFilter: billingAccountTableFilterApply
            };
            
            var billingAccountItems = await httpPost('external/finance/getBillingAccountItems', data);

            dispatch({
                type: getBillingAccount,
                billingAccountItems: Object.keys(billingAccountItems).length > 0 ? billingAccountItems : DEFAULT_TABLE_RESULT_MODEL,
                isBillingAccountLoaded: true,
                isBillingAccountError: false
            });
        }
        catch (e) {
            dispatch({
                type: setBillingAccountError,
                isBillingAccountError: true,
                isBillingAccountLoaded: false
            })
        }
    },

    getBillingComments: (financeAccountID, pageIndex = 1, pageSize = 5) => async (dispatch, state) => {
        const existingBillingComments = state().billingAccount.billingComments || [];

        const lastCommentId = existingBillingComments.length > 0
            ? existingBillingComments[existingBillingComments.length - 1].CommentID
            : null;


        const timezoneOffset = new Date().getTimezoneOffset() * -1;
        var res = await httpGet(
            `external/finance/getBillingComments/${pageIndex}/${pageSize}?financeAccountID=${financeAccountID}&timeZoneOffset=${timezoneOffset}` +
            (lastCommentId != null ? `&lastCommentId=${lastCommentId}` : "")
        );


        if (res != null) {
            dispatch({
                type: getBillingComments,
                billingComments: res.comments,
                isBillingAccountLoaded: true,
                isBillingAccountError: false,
                pageIndex: pageIndex,
                hasMoreData: res.hasMoreData
            });
        } else {
            dispatch({
                type: setBillingAccountError,
                isBillingAccountError: true,
                isBillingAccountLoaded: false
            });
        }
    },

    postBillingComments: (financeAccountID, filename, fileData, comment, pageIndex = 1, pageSize = 5) => async (dispatch, state) => {
        const timezoneOffset = new Date().getTimezoneOffset() * -1;

        var formData = new FormData();

        if (fileData && fileData.length > 0) {
            fileData.forEach((element) => formData.append("FileData", element));
            formData.append("FileName", JSON.stringify(filename));
        }

        formData.append("Comment", comment.toString());
        formData.append("UserId", parseInt(sessionStorage.getItem("UserID")));
        formData.append("FinanceAccountID", financeAccountID);

        var res = await httpPostFile(
            `external/finance/setBillingComments/${pageIndex}/${pageSize}?timeZoneOffset=${timezoneOffset}`,
            formData
        );

        if (res != null) {
            dispatch({
                type: insertBillingComments,
                billingComments: res.comment,
                isBillingAccountLoaded: true,
                isBillingAccountError: false,
            });
        } else {
            dispatch({
                type: setBillingAccountError,
                isBillingAccountError: true,
                isBillingAccountLoaded: false,
            });
        }
    },

    deleteBillingComments: (commentId) => async (dispatch, state) => {
        const userId = sessionStorage.getItem("UserID")
        const res = await httpPost('external/finance/billingDeleteComment/' + commentId + '/' + userId);

        if (res == null) {
            dispatch({
                type: setBillingAccountError,
                isInvoiceDisputeError: true,
                isInvoiceDisputeLoaded: false
            });
        }
    },

    clearBillingAccountComments: () => async (dispatch, state) => {
        dispatch({
            type: resetBillingComments,
            billingComments: [],
        })
    },

    extractBillingAccount: () => async (dispatch, state) => {
        const { Headers } = state().billingAccount.billingAccountItems;
        const { billingAccountColSort, billingAccountIsSortedAsc, billingAccountTableFilterApply, billingAccountTableFilterList } = state().billingAccount;
        const dealName = sessionStorage.getItem('DealName');
        const { activeDateFormat, activeTimeZoneOffset } = state().adminSetting;
        const formattedTimeZoneDate = convertToFormatTimeZoneDate(
            new Date(),
            activeDateFormat?.Name || "YYYY-MM-DD",
            activeTimeZoneOffset?.UtcOffset || 0)
            .replace(/[<>:"/\|?*]/g, '-');
        const filename = `ATEM ${dealName} Billing Account Extract – ${formattedTimeZoneDate}`;
        var data = {
            Eid: JSON.parse(sessionStorage.getItem('User')).Employee.EnterpriseId,
            UserId: sessionStorage.getItem('UserID'),
            EmployeeId: sessionStorage.getItem('EmployeeID'),
            Headers: {
                ...Headers
            },

            SortedColumnName: billingAccountColSort,
            IsSortAscending: billingAccountIsSortedAsc,
            TableFilterList: billingAccountTableFilterList,
            ApplyFilter: billingAccountTableFilterApply,


        };
        
        httpDownload('external/finance/getAllBillingAccountExtractItems', data)
            .then(function (response) {
                return response.blob();
            })
            .then(function (blob) {
                let downloadUrl = window.URL.createObjectURL(blob);
                //Hide Loading
                let a = document.createElement('a');
                if (window.navigator.msSaveOrOpenBlob) {
                    window.navigator.msSaveOrOpenBlob(blob, filename);
                } else if (typeof a.download === 'undefined') {
                    window.location = downloadUrl;
                } else {
                    a.href = downloadUrl;
                    a.download = filename + '.xlsx';
                    document.body.appendChild(a);
                    a.click();
                }
            });
    },

    setBillingAccountColSearchStr: (event, key) => async (dispatch, state) => {
        var val = event.target.value;
        const { billingAccountItems } = state().billingAccount;
        var billingAccountListUpd = {
            ...billingAccountItems,
            "Headers": {
                ...billingAccountItems.Headers,
                [key]: {
                    ...billingAccountItems.Headers[key],
                    searchString: val
                }
            }
        }

        dispatch({
            type: setBillingAccountColSearchStr,
            data: billingAccountListUpd,
            isBillingAccountLoaded: true,
            isBillingAccountError: false
        });
    },

    sortBillingAccountList: (columnClicked, isSortedAsc) => async (dispatch, state) => {
        const { PageNumber, PageSizes } = state().billingAccount.billingAccountItems.Pagination;
        const { billingAccountItems, billingAccountTableFilterList, billingAccountTableFilterApply } = state().billingAccount;

        var data = {
            PageFilter: {
                PageIndex: PageNumber !== undefined ? PageNumber : 0,
                PageSize: billingAccountItems.Pagination.PerPage,
                PageSizes: PageSizes
            },
            Eid: JSON.parse(sessionStorage.getItem('User')).Employee.EnterpriseId,
            UserId: sessionStorage.getItem('UserID'),
            EmployeeId: sessionStorage.getItem('EmployeeID'),
            Headers: {
                ...billingAccountItems.Headers
            },
            SortedColumnName: columnClicked,
            IsSortAscending: isSortedAsc,
            TableFilterList: billingAccountTableFilterList,
            ApplyFilter: billingAccountTableFilterApply
        };

        var resBillingAccountList = await httpPost('external/finance/getBillingAccountItems', data);
        if (resBillingAccountList.ok === false) {
            dispatch({
                type: setBillingAccountError,
                isBillingAccountError: true,
                isBillingAccountLoaded: false
            });
        } else {
            dispatch({
                type: sortBillingAccountList,
                billingAccountItems: Object.keys(resBillingAccountList).length > 0 ? resBillingAccountList : DEFAULT_TABLE_RESULT_MODEL,
                billingAccountColSort: columnClicked,
                billingAccountIsSortedAsc: isSortedAsc,
                isBillingAccountLoaded: true
            })
        }
    },

    resizeBillingAccountPage: (pageSize) => async (dispatch, state) => {
        dispatch({
            type: setBillingAccountListPageSizes,
            pageSize: parseInt(pageSize),
        });
    },

    saveBillingAccountCustomCols: (selectedCols) => async (dispatch, state) => {
        const { Headers, Pagination } = state().billingAccount.billingAccountItems;
        const { billingAccountColSort, billingAccountIsSortedAsc } = state().billingAccount;

        try {
            var userId = sessionStorage.getItem('UserID');

            var customizeFormData = {
                UserId: parseInt(userId),
                ReportColumnModule: REPORT_COLUMN_MODULE.BillingAccountFinanceManagement,
                SelectedCols: selectedCols
            };

            await httpPost('external/finance/saveBillingAccountCustomizeColumns', customizeFormData)
            dispatch({
                type: setBillingAccountError,
                isBillingAccountError: false,
                isBillingAccountLoaded: false
            });

            var data = {
                PageFilter: {
                    PageIndex: 0,
                    PageSize: Pagination.PerPage,
                    PageSizes: Pagination.PageSizes
                },
                Eid: JSON.parse(sessionStorage.getItem('User')).Employee.EnterpriseId,
                UserId: userId,
                EmployeeId: sessionStorage.getItem('EmployeeID'),
                Headers: {
                    ...Headers
                },
                SortedColumnName: billingAccountColSort,
                IsSortAscending: billingAccountIsSortedAsc
            };
            
            var billingAccountItems = await httpPost('external/finance/getBillingAccountItems', data);

            dispatch({
                type: getBillingAccount,
                billingAccountItems: Object.keys(billingAccountItems).length > 0 ? billingAccountItems : DEFAULT_TABLE_RESULT_MODEL,
                isBillingAccountLoaded: true,
                isBillingAccountError: false
            });
        }
        catch (e) {
            dispatch({
                type: setBillingAccountError,
                isBillingAccountError: true,
                isBillingAccountLoaded: false
            });
        }
    },

    addBillingAccountFilter: (tableFilter) => (dispatch, state) => {
        const { billingAccountTableFilterList } = state().billingAccount;
        var filterList = [...billingAccountTableFilterList, tableFilter];

        dispatch({
            type: setBillingAccountFilter,
            filterList: filterList
        })
    },

    removeBillingAccountFilter: (removeIndex) => (dispatch, state) => {
        const { billingAccountTableFilterList } = state().billingAccount;

        var filterList = billingAccountTableFilterList.filter((item, index) => {
            return index !== removeIndex;
        });

        dispatch({
            type: setBillingAccountFilter,
            filterList: filterList
        })
    },

    applyBillingAccountFilter: (isApply) => (dispatch, state) => {
        const { billingAccountTableFilterList } = state().billingAccount;

        dispatch({
            type: applyBillingAccountFilter,
            applyFilter: isApply,
            filterList: isApply ? billingAccountTableFilterList : []
        });
    },
};

export const reducer = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case getBillingAccount:
            return {
                ...state,
                billingAccountItems: action.billingAccountItems,
                isBillingAccountLoaded: action.isBillingAccountLoaded,
                isBillingAccountError: action.isBillingAccountError
            };
        case setBillingAccountError:
            return {
                ...state,
                isBillingAccountLoaded: action.isBillingAccountLoaded,
                isBillingAccountError: action.isBillingAccountError
            };
        case setBillingAccountColSearchStr:
            return {
                ...state,
                billingAccountItems: action.data,
                isBillingAccountLoaded: action.isBillingAccountLoaded,
                isBillingAccountError: action.isBillingAccountError
            };
        case sortBillingAccountList:
            return {
                ...state,
                billingAccountItems: action.billingAccountItems,
                billingAccountColSort: action.billingAccountColSort,
                billingAccountIsSortedAsc: action.billingAccountIsSortedAsc,
                isBillingAccountLoaded: action.isBillingAccountLoaded
            };
        case setBillingAccountListPageSizes:
            return {
                ...state,
                billingAccountItems: {
                    ...state.billingAccountItems,
                    Pagination: {
                        ...state.billingAccountItems.Pagination,
                        PerPage: action.pageSize
                    }
                }
            };
        case setBillingAccountFilter:
            return {
                ...state,
                billingAccountTableFilterList: action.filterList,
            };
        case applyBillingAccountFilter:
            return {
                ...state,
                billingAccountTableFilterApply: action.applyFilter,
                billingAccountTableFilterList: action.filterList
            };
        case getBillingComments:
            return {
                ...state,
                billingComments: [
                    ...state.billingComments,
                    ...action.billingComments
                ],
                hasMoreComments: action.hasMoreData,
                pageIndex: action.pageIndex
            };
        case resetBillingComments: {
            return {
                ...state,
                billingComments: action.billingComments,
                hasMoreComments: false,
                pageIndex: 1
            }
        }
        case insertBillingComments: {

            const combinedComments = [
                ...action.billingComments,
                ...state.billingComments
            ];

            const currentVisibleCount = state.billingComments.length;
            const currentDisplayLimit = Math.ceil(currentVisibleCount / 5) * 5;
            const hasMore = combinedComments.length > currentDisplayLimit && combinedComments.length !== 1;
            let updatedComments;
            if (hasMore) {
                updatedComments = combinedComments.slice(0, -1);
            } else {
                updatedComments = combinedComments;
            }

            return {
                ...state,
                billingComments: combinedComments.length === 1 ? action.billingComments : updatedComments,
                hasMoreComments: hasMore,
            };
        }
        case setDefaultBillingAccount:
            return initialState;
        default:
            return state;
    }
}